import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Lightbulb, X } from "lucide-react"
import { useEffect, useState } from "react"
import type { FrameworkDerivation } from "../mapping/deriveFrameworkVerdict"

/** The one localStorage key this coachmark ever touches — "1" once dismissed,
 *  absent otherwise. Never written except by an explicit dismiss (the
 *  "Verstanden" button or the close X) — see `dismiss` below. */
const STORAGE_KEY = "turnus.sv2.coachR1Dismissed"

/**
 * Whether the coachmark has already been SHOWN once this session — the
 * fallback for when localStorage read/write fails (private browsing, quota,
 * storage disabled entirely): even then, the coachmark never shows a second
 * time before a real page reload. Set the instant a mount decides to render
 * it (not only on an explicit dismiss), so switching the active Verordnung
 * away and back to a fresh R1 one does not resurface it either. Module
 * scope, mirroring `HelpPopover`'s own `autoOpened` (same "once per session,
 * not per mount" problem, same fix).
 */
let shownThisSession = false

function readDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1"
  } catch {
    // Storage inaccessible (private mode, disabled entirely) — treated as
    // "not dismissed"; `shownThisSession` is what keeps this from nagging
    // twice in the same session regardless.
    return false
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "1")
  } catch {
    // Same storage-unavailable case as above — `shownThisSession` still
    // holds for the rest of THIS session; a future session may show the
    // coachmark again, an acceptable degrade rather than a crash.
  }
}

export interface R1CoachmarkProps {
  derivation: FrameworkDerivation | null
  /** Shows the coachmark even when `derivation` is not a genuine R1 rule —
   *  `UnitCard`'s own "Unklar → Rückfrage" pick is LOCAL display state
   *  (`manualGeltung`) that never flips the framework's derivation, so that
   *  moment has no R1 derivation to gate on. Once-only semantics (the
   *  session flag, the localStorage dismissal) are unchanged — this only
   *  widens the condition that decides whether to render at all. */
  force?: boolean
}

/**
 * A one-time contextual coachmark for the R1 state ("Unklar · Rückfrage
 * nötig/offen" — the derivation's own R1 branch) — the moment most likely to
 * strand a first-time reviewer, and the one `HelpPopover`'s 3 generic steps
 * never mention. Deliberately NOT a bigger help popover: a small,
 * dismissible, once-only card instead.
 *
 * MOUNTED FROM `ReviewMaskPane`, in the normal-flow seam between `.sv2-pnh`
 * (a fixed-height `47px` flex row that cannot host a block card) and
 * `.sv2-pbody`, reading the mask's `derivation` prop directly. The framework
 * head pill that used to sit in that row was removed (TUR-1211, 2026-09-13);
 * the coachmark is now the only surface that speaks about R1 up here.
 *
 * ONCE EVER, NOT ONCE PER VERORDNUNG. `dismissed` starts from
 * `readDismissed() || shownThisSession` — the OR is deliberate: a fresh
 * mount (new declaration, new session) with no stored flag but a session
 * that already showed it once (a different R1 Verordnung, storage write
 * failed) starts dismissed too, so it never nags twice in one session even
 * without storage. Once rendered and not yet dismissed, it STAYS visible
 * across the reviewer switching to another R1 Verordnung and back — it was
 * never dismissed, so nothing should make it disappear on its own; only an
 * explicit `dismiss()` (button or X) hides it and persists that.
 */
export function R1Coachmark({ derivation, force = false }: R1CoachmarkProps) {
  const { translate } = useTranslation()
  const isR1 = derivation?.rule === "R1" || force
  const [dismissed, setDismissed] = useState(
    () => readDismissed() || shownThisSession,
  )

  useEffect(() => {
    if (isR1 && !dismissed) shownThisSession = true
  }, [isR1, dismissed])

  if (!isR1 || dismissed) return null

  const dismiss = () => {
    setDismissed(true)
    shownThisSession = true
    writeDismissed()
  }

  return (
    <div className="sv2-coachr1" role="note" aria-live="polite">
      <Lightbulb className="sv2-ic" />
      <div className="sv2-cr1-body">
        <div className="sv2-cr1-title">
          {translate("MC_DECL_V2_COACH_R1_TITLE")}
        </div>
        <div className="sv2-cr1-text">
          {translate("MC_DECL_V2_COACH_R1_BODY")}
        </div>
        <div className="sv2-cr1-actions">
          <button
            type="button"
            data-testid="r1-coachmark-dismiss"
            className="sv2-btn sv2-btn-sm sv2-btn-primary"
            onClick={dismiss}
          >
            {translate("MC_DECL_V2_COACH_R1_DISMISS")}
          </button>
        </div>
      </div>
      <button
        type="button"
        data-testid="r1-coachmark-close"
        aria-label={translate("MC_DECL_V2_COACH_R1_CLOSE_ARIA")}
        className="sv2-cr1-close"
        onClick={dismiss}
      >
        <X className="sv2-ic" />
      </button>
    </div>
  )
}

/** Test-only reset — a session boundary this module cannot see otherwise
 *  (mirrors `HelpPopover`'s `resetHelpPopoverAutoOpen`). */
export function resetR1CoachmarkSession(): void {
  shownThisSession = false
}
